import React from 'react'
import { toLower } from 'ramda'
import { Icon, IconSize, IconType } from 'sidecar-components/atoms/Icon'

import { HeaderModal } from './HeaderModal'
import { Provider } from './PreCheckedProviders.types'

interface HeaderProps {
  /** Description of the service category */
  description: string
  /** Whether the sidecar code type is Facility */
  isFacility: boolean
  /** Whether the sidecar code type is Professional */
  isProfessional: boolean
  /** Pre-checked category name from Contentful */
  preCheckedCategoryName?: string
  /** Array of professional providers */
  professionalProviders: Provider[]
  /** Array of all providers */
  providers: Provider[]
  /** Type of sidecar code (Professional or Facility) */
  sidecarCodeType: 'Professional' | 'Facility'
  /** Type of component usage context */
  type:
    | 'careDetail'
    | 'careSearch'
    | 'episodeOfCare'
    | 'publicCareDetail'
    | 'requestForm'
  /** Whether the component is used in Premember */
  premember?: boolean
  /** Hide the "Pre-checked providers" heading */
  hideHeading?: boolean
}

export const Header: React.FC<HeaderProps> = ({
  description,
  isFacility,
  isProfessional,
  preCheckedCategoryName = '',
  professionalProviders,
  providers,
  sidecarCodeType,
  type,
  premember,
  hideHeading = false,
}) => {
  const sidecarCodeText = isProfessional ? 'provider' : 'facility'
  const testId = `precheckedHeaderTooltip_${toLower(sidecarCodeText)}`

  // Header text using extracted ternaries to prevent warnings
  const preCheckedText = isProfessional ? 'providers' : 'facilities'
  const headerText = `Pre-checked ${preCheckedText}`

  // When the component is in a modal or poptart,
  // its header should have a smaller title
  const HeadingTag =
    type === 'publicCareDetail' || type === 'requestForm' ? 'h4' : 'h3'

  return (
    <>
      {!hideHeading && (
        <HeadingTag
          className="inherit flex gap-2 items-center"
          data-testid={`info_tileHeader_${testId}`}
        >
          {/* The requestForm is use in the member comm poptart
        The poptart does not need the badge icon */}
          {type === 'requestForm' && 'Did you try these Pre-checked providers?'}

          {type !== 'requestForm' && (
            <>
              <Icon
                name="verify"
                size={IconSize.MD}
                type={IconType.SYSTEM}
                className="color-iconAccent-default"
              />
              {headerText}
            </>
          )}

          <HeaderModal
            description={description}
            isFacility={isFacility}
            isProfessional={isProfessional}
            professionalProviders={professionalProviders}
            providers={providers}
            sidecarCodeType={sidecarCodeType}
            type={type}
            premember={premember}
          />
        </HeadingTag>
      )}

      {/* The requestForm is use in the member comm poptart */}
      {type === 'requestForm' && (
        <p>
          You won't pay anything for
          {preCheckedCategoryName === 'labs'
            ? ' lab '
            : ` ${preCheckedCategoryName} `}
          services at these pre-checked providers if your deductible has been
          met or doesn't apply. If the deductible applies, you'll be responsible
          for the entire amount of the provider's charge.
        </p>
      )}
    </>
  )
}
